// src/components/Dashboard_ADM/GerenciamentoNovidades.tsx
// CREATED: 2026-03-27
// Página ADM para criar, editar, publicar e remover novidades
// que aparecem nos portais do aluno e do filiado

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Plus, Loader2, AlertTriangle, RefreshCw, X,
  Image, Link, Mail, Phone, Globe, Eye, EyeOff,
  Trash2, Edit3, CheckCircle, Clock, Upload,
  Megaphone, Wrench, Info, Sparkles, Tag,
  Send,
} from 'lucide-react';
import { API_BASE, authH } from '../../utils/apiConfig';

// ─── Types ────────────────────────────────────────────────────────────────────
type TipoNovidade = 'servico' | 'informacao' | 'novidade' | 'atualizacao' | 'promocao';
type StatusNovidade = 'draft' | 'published';

interface Novidade {
  id:           string;
  tipo:         TipoNovidade;
  titulo:       string;
  descricao:    string;
  imageUrl:     string | null;
  status:       StatusNovidade;
  botaoLabel:   string | null;
  botaoContato: string | null;
  botaoSite:    string | null;
  botaoEmail:   string | null;
  createdAt:    string;
}

interface FormState {
  tipo:         TipoNovidade;
  titulo:       string;
  descricao:    string;
  imageUrl:     string;
  status:       StatusNovidade;
  botaoLabel:   string;
  botaoContato: string;
  botaoSite:    string;
  botaoEmail:   string;
}

// ─── Configs visuais por tipo ─────────────────────────────────────────────────
const TIPO_CFG: Record<TipoNovidade, {
  label: string; icon: React.FC<any>;
  bg: string; text: string; border: string; dot: string;
}> = {
  servico:     { label: 'Serviço',      icon: Wrench,    bg: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-200',    dot: '#3b82f6' },
  informacao:  { label: 'Informação',   icon: Info,      bg: 'bg-sky-50',     text: 'text-sky-700',     border: 'border-sky-200',     dot: '#0ea5e9' },
  novidade:    { label: 'Novidade',     icon: Megaphone, bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200',   dot: '#f59e0b' },
  atualizacao: { label: 'Atualização',  icon: Sparkles,  bg: 'bg-purple-50',  text: 'text-purple-700',  border: 'border-purple-200',  dot: '#a855f7' },
  promocao:    { label: 'Promoção',     icon: Tag,       bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: '#22c55e' },
};

const INITIAL_FORM: FormState = {
  tipo: 'novidade', titulo: '', descricao: '', imageUrl: '',
  status: 'draft', botaoLabel: '', botaoContato: '', botaoSite: '', botaoEmail: '',
};

// ─── Componente ───────────────────────────────────────────────────────────────
const GerenciamentoNovidades: React.FC = () => {
  const [novidades,    setNovidades]    = useState<Novidade[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [erro,         setErro]         = useState<string | null>(null);
  const [modalAberto,  setModalAberto]  = useState(false);
  const [editando,     setEditando]     = useState<Novidade | null>(null);
  const [form,         setForm]         = useState<FormState>(INITIAL_FORM);
  const [salvando,     setSalvando]     = useState(false);
  const [erroForm,     setErroForm]     = useState<string | null>(null);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [actionId,     setActionId]     = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // ── Carrega lista ─────────────────────────────────────────────────────────
  const carregar = useCallback(async () => {
    setLoading(true); setErro(null);
    try {
      const res = await fetch(`${API_BASE}/novidades/all?limit=50`, { headers: authH() });
      if (!res.ok) throw new Error(`Erro ${res.status}`);
      const data = await res.json();
      setNovidades(data.novidades ?? []);
    } catch (e: any) {
      setErro(e.message ?? 'Erro ao carregar');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // ── Abre modal ────────────────────────────────────────────────────────────
  const abrirCriar = () => {
    setEditando(null);
    setForm(INITIAL_FORM);
    setImagePreview(null);
    setErroForm(null);
    setModalAberto(true);
  };

  const abrirEditar = (n: Novidade) => {
    setEditando(n);
    setForm({
      tipo:         n.tipo,
      titulo:       n.titulo,
      descricao:    n.descricao,
      imageUrl:     n.imageUrl ?? '',
      status:       n.status,
      botaoLabel:   n.botaoLabel   ?? '',
      botaoContato: n.botaoContato ?? '',
      botaoSite:    n.botaoSite    ?? '',
      botaoEmail:   n.botaoEmail   ?? '',
    });
    setImagePreview(n.imageUrl ?? null);
    setErroForm(null);
    setModalAberto(true);
  };

  const fecharModal = () => { setModalAberto(false); setEditando(null); setErroForm(null); };

  const set = (k: keyof FormState, v: string) =>
    setForm(p => ({ ...p, [k]: v }));

  // ── Upload de imagem ──────────────────────────────────────────────────────
  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setImagePreview(ev.target?.result as string);
    reader.readAsDataURL(file);

    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('type', 'novidade');
      const res = await fetch(`${API_BASE}/documents/upload`, {
        method: 'POST',
        headers: { Authorization: (authH() as any).Authorization },
        body: fd,
      });
      if (res.ok) {
        const data = await res.json();
        set('imageUrl', data.url ?? data.fileUrl ?? data.path ?? '');
      }
    } catch { /* preview fica */ }
    finally { setUploadingImg(false); }
  };

  // ── Salvar (criar ou editar) ──────────────────────────────────────────────
  const salvar = async () => {
    if (!form.titulo.trim())   { setErroForm('Título é obrigatório'); return; }
    if (!form.descricao.trim()){ setErroForm('Descrição é obrigatória'); return; }
    setSalvando(true); setErroForm(null);
    try {
      const body = {
        tipo:         form.tipo,
        titulo:       form.titulo.trim(),
        descricao:    form.descricao.trim(),
        imageUrl:     form.imageUrl.trim() || null,
        status:       form.status,
        botaoLabel:   form.botaoLabel.trim()   || null,
        botaoContato: form.botaoContato.trim() || null,
        botaoSite:    form.botaoSite.trim()    || null,
        botaoEmail:   form.botaoEmail.trim()   || null,
      };
      const url    = editando ? `${API_BASE}/novidades/${editando.id}` : `${API_BASE}/novidades`;
      const method = editando ? 'PATCH' : 'POST';
      const res    = await fetch(url, { method, headers: authH(), body: JSON.stringify(body) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(Array.isArray(err.message) ? err.message.join(' · ') : err.message ?? `Erro ${res.status}`);
      }
      fecharModal();
      carregar();
    } catch (e: any) {
      setErroForm(e.message ?? 'Erro ao salvar');
    } finally { setSalvando(false); }
  };

  // ── Publicar / despublicar ────────────────────────────────────────────────
  const togglePublish = async (n: Novidade) => {
    setActionId(n.id);
    try {
      const endpoint = n.status === 'published' ? 'unpublish' : 'publish';
      await fetch(`${API_BASE}/novidades/${n.id}/${endpoint}`, { method: 'POST', headers: authH() });
      carregar();
    } finally { setActionId(null); }
  };

  // ── Remover ───────────────────────────────────────────────────────────────
  const remover = async (n: Novidade) => {
    if (!confirm(`Remover "${n.titulo}"?`)) return;
    setActionId(n.id);
    try {
      await fetch(`${API_BASE}/novidades/${n.id}`, { method: 'DELETE', headers: authH() });
      carregar();
    } finally { setActionId(null); }
  };

  const publicadas = novidades.filter(n => n.status === 'published');
  const rascunhos  = novidades.filter(n => n.status === 'draft');

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <div className="relative flex-shrink-0">
            <div className="absolute -inset-1 bg-amber-400/20 blur-[10px] rounded-full"/>
            <div className="relative w-11 h-11 rounded-2xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg,#f4d03f,#d4af37)' }}>
              <Megaphone className="w-5 h-5 text-[#1a1a1c]"/>
            </div>
          </div>
          <div>
            <h1 className="text-[22px] font-semibold text-gray-900 tracking-tight leading-none">Novidades</h1>
            <p className="text-xs text-gray-400 mt-1">Publique atualizações nos portais do aluno e do filiado</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={carregar} className="p-2.5 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <RefreshCw className="w-4 h-4"/>
          </button>
          <button onClick={abrirCriar}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-[#1a1a1c] hover:shadow-lg transition-all"
            style={{ background: 'linear-gradient(135deg,#f4d03f,#d4af37)' }}>
            <Plus className="w-4 h-4"/> Nova publicação
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Total',      value: novidades.length,   dot: '#6b7280' },
          { label: 'Publicadas', value: publicadas.length,  dot: '#22c55e' },
          { label: 'Rascunhos',  value: rascunhos.length,   dot: '#f59e0b' },
        ].map(({ label, value, dot }) => (
          <div key={label} className="bg-white rounded-2xl border border-gray-100 shadow-[0_1px_6px_rgba(0,0,0,0.04)] p-4 flex items-center gap-3">
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: dot }}/>
            <div>
              <p className="text-xl font-bold text-gray-900 leading-none">{value}</p>
              <p className="text-xs text-gray-400 mt-0.5">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Loading / Erro */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-7 h-7 text-amber-500 animate-spin"/>
        </div>
      )}
      {erro && !loading && (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-2xl">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0"/>
          <p className="text-sm text-red-700">{erro}</p>
        </div>
      )}

      {/* Lista */}
      {!loading && !erro && (
        <div className="space-y-3">
          {novidades.length === 0 && (
            <div className="bg-white rounded-2xl border border-dashed border-gray-200 py-16 text-center">
              <Megaphone className="w-12 h-12 text-gray-200 mx-auto mb-3"/>
              <p className="text-sm font-medium text-gray-500">Nenhuma novidade cadastrada</p>
              <p className="text-xs text-gray-400 mt-1">Clique em "Nova publicação" para começar</p>
            </div>
          )}
          {novidades.map(n => {
            const cfg  = TIPO_CFG[n.tipo] ?? TIPO_CFG.novidade;
            const Icon = cfg.icon;
            const isActing = actionId === n.id;
            return (
              <div key={n.id}
                className="bg-white rounded-2xl border border-gray-100 shadow-[0_1px_6px_rgba(0,0,0,0.04)]
                  hover:shadow-[0_4px_16px_rgba(0,0,0,0.08)] transition-all overflow-hidden">
                <div className="flex items-start gap-4 p-5">
                  {/* Imagem ou ícone */}
                  <div className="flex-shrink-0">
                    {n.imageUrl ? (
                      <img src={n.imageUrl} alt={n.titulo}
                        className="w-16 h-16 rounded-xl object-cover border border-gray-100"/>
                    ) : (
                      <div className="w-16 h-16 rounded-xl flex items-center justify-center"
                        style={{ background: cfg.dot + '18' }}>
                        <Icon className="w-7 h-7" style={{ color: cfg.dot }}/>
                      </div>
                    )}
                  </div>

                  {/* Conteúdo */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
                        {cfg.label}
                      </span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border flex items-center gap-1 ${
                        n.status === 'published'
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : 'bg-gray-50 text-gray-500 border-gray-200'
                      }`}>
                        {n.status === 'published'
                          ? <><CheckCircle className="w-2.5 h-2.5"/> Publicada</>
                          : <><Clock className="w-2.5 h-2.5"/> Rascunho</>}
                      </span>
                    </div>
                    <p className="font-semibold text-gray-900 text-sm truncate">{n.titulo}</p>
                    <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{n.descricao}</p>
                    {(n.botaoLabel || n.botaoContato || n.botaoSite || n.botaoEmail) && (
                      <p className="text-[10px] text-amber-600 mt-1.5 flex items-center gap-1">
                        <Link className="w-3 h-3"/> Botão "Sobre" configurado
                      </p>
                    )}
                  </div>

                  {/* Ações */}
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button onClick={() => abrirEditar(n)} disabled={isActing}
                      className="p-2 rounded-xl hover:bg-gray-100 transition-colors text-gray-400 disabled:opacity-50">
                      <Edit3 className="w-4 h-4"/>
                    </button>
                    <button onClick={() => togglePublish(n)} disabled={isActing}
                      className={`p-2 rounded-xl transition-colors disabled:opacity-50 ${
                        n.status === 'published'
                          ? 'hover:bg-amber-50 text-amber-500'
                          : 'hover:bg-emerald-50 text-emerald-500'
                      }`}
                      title={n.status === 'published' ? 'Despublicar' : 'Publicar'}>
                      {isActing
                        ? <Loader2 className="w-4 h-4 animate-spin"/>
                        : n.status === 'published' ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                    </button>
                    <button onClick={() => remover(n)} disabled={isActing}
                      className="p-2 rounded-xl hover:bg-red-50 text-gray-300 hover:text-red-400 transition-colors disabled:opacity-50">
                      <Trash2 className="w-4 h-4"/>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── MODAL CRIAR / EDITAR ─────────────────────────────────────────── */}
      {modalAberto && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-auto shadow-2xl">

            {/* Header modal */}
            <div className="sticky top-0 bg-white z-10 p-6 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg,#f4d03f,#d4af37)' }}>
                  {editando ? <Edit3 className="w-4 h-4 text-[#1a1a1c]"/> : <Plus className="w-4 h-4 text-[#1a1a1c]"/>}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{editando ? 'Editar' : 'Nova'} Publicação</h3>
                  <p className="text-xs text-gray-400">Aparecerá nos portais do aluno e do filiado</p>
                </div>
              </div>
              <button onClick={fecharModal} disabled={salvando} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X className="w-5 h-5"/>
              </button>
            </div>

            <div className="p-6 space-y-5">

              {/* Tipo */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Tipo de publicação <span className="text-red-400">*</span>
                </label>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {(Object.entries(TIPO_CFG) as [TipoNovidade, typeof TIPO_CFG[TipoNovidade]][]).map(([key, cfg]) => {
                    const Icon = cfg.icon;
                    const sel  = form.tipo === key;
                    return (
                      <button key={key} type="button" onClick={() => set('tipo', key)}
                        className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all text-xs font-semibold ${
                          sel
                            ? `${cfg.bg} ${cfg.text} ${cfg.border} shadow-sm`
                            : 'border-gray-200 text-gray-500 hover:border-gray-300'
                        }`}>
                        <Icon className="w-5 h-5"/>
                        {cfg.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Título */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Título <span className="text-red-400">*</span>
                </label>
                <input
                  type="text" value={form.titulo}
                  onChange={e => set('titulo', e.target.value)}
                  placeholder="Ex: Nova parceria disponível para filiados"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm text-gray-900 font-medium focus:ring-2 focus:ring-amber-400 focus:border-transparent transition"
                  maxLength={120}
                />
                <p className="text-right text-[10px] text-gray-300 mt-1">{form.titulo.length}/120</p>
              </div>

              {/* Descrição */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Descrição <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={form.descricao}
                  onChange={e => set('descricao', e.target.value)}
                  rows={4}
                  placeholder="Descreva a novidade, serviço ou informação..."
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm text-gray-900 focus:ring-2 focus:ring-amber-400 focus:border-transparent transition resize-none"
                />
              </div>

              {/* Imagem */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  <Image className="inline w-3.5 h-3.5 mr-1"/> Imagem (opcional)
                </label>
                <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden"/>
                <div className="flex gap-3">
                  <input
                    type="url" value={form.imageUrl}
                    onChange={e => { set('imageUrl', e.target.value); setImagePreview(e.target.value || null); }}
                    placeholder="https://... ou use o botão para upload"
                    className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl text-sm text-gray-900 focus:ring-2 focus:ring-amber-400 focus:border-transparent transition"
                  />
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploadingImg}
                    className="px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-400 hover:border-amber-400 hover:text-amber-600 transition-all flex items-center gap-2 text-sm font-medium disabled:opacity-50">
                    {uploadingImg ? <Loader2 className="w-4 h-4 animate-spin"/> : <Upload className="w-4 h-4"/>}
                    Upload
                  </button>
                </div>
                {imagePreview && (
                  <div className="mt-3 relative inline-block">
                    <img src={imagePreview} alt="Preview"
                      className="h-28 object-cover rounded-xl border-2 border-amber-200"
                      onError={() => setImagePreview(null)}/>
                    <button type="button" onClick={() => { setImagePreview(null); set('imageUrl', ''); }}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs font-bold flex items-center justify-center hover:bg-red-600">
                      ✕
                    </button>
                  </div>
                )}
              </div>

              {/* Botão "Sobre" */}
              <div className="rounded-2xl border border-gray-100 bg-gray-50 p-5 space-y-4">
                <div className="flex items-center gap-2">
                  <Link className="w-4 h-4 text-amber-600"/>
                  <p className="text-sm font-semibold text-gray-800">Botão "Sobre" (opcional)</p>
                </div>
                <p className="text-xs text-gray-400 -mt-2">
                  Adicione um botão de ação para que os usuários entrem em contato ou acessem mais informações.
                </p>

                <div>
                  <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                    Texto do botão
                  </label>
                  <input type="text" value={form.botaoLabel}
                    onChange={e => set('botaoLabel', e.target.value)}
                    placeholder='Ex: "Saiba mais", "Fale conosco", "Acessar"'
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm text-gray-900 bg-white focus:ring-2 focus:ring-amber-400 focus:border-transparent transition"
                    maxLength={40}/>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {/* Contato / WhatsApp */}
                  <div>
                    <label className="flex items-center gap-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                      <Phone className="w-3 h-3"/> Telefone / WhatsApp
                    </label>
                    <input type="text" value={form.botaoContato}
                      onChange={e => set('botaoContato', e.target.value)}
                      placeholder="(11) 99999-9999"
                      className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-xs text-gray-900 bg-white focus:ring-2 focus:ring-amber-400 focus:border-transparent transition"/>
                  </div>
                  {/* Site */}
                  <div>
                    <label className="flex items-center gap-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                      <Globe className="w-3 h-3"/> Site / Link
                    </label>
                    <input type="url" value={form.botaoSite}
                      onChange={e => set('botaoSite', e.target.value)}
                      placeholder="https://..."
                      className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-xs text-gray-900 bg-white focus:ring-2 focus:ring-amber-400 focus:border-transparent transition"/>
                  </div>
                  {/* Email */}
                  <div>
                    <label className="flex items-center gap-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                      <Mail className="w-3 h-3"/> E-mail
                    </label>
                    <input type="email" value={form.botaoEmail}
                      onChange={e => set('botaoEmail', e.target.value)}
                      placeholder="contato@..."
                      className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-xs text-gray-900 bg-white focus:ring-2 focus:ring-amber-400 focus:border-transparent transition"/>
                  </div>
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Status de publicação
                </label>
                <div className="flex gap-3">
                  {(['draft', 'published'] as StatusNovidade[]).map(s => (
                    <button key={s} type="button" onClick={() => set('status', s)}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${
                        form.status === s
                          ? s === 'published'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                            : 'bg-gray-100 text-gray-700 border-gray-300'
                          : 'border-gray-200 text-gray-400 hover:border-gray-300'
                      }`}>
                      {s === 'published' ? <><CheckCircle className="w-4 h-4"/> Publicar agora</> : <><Clock className="w-4 h-4"/> Salvar como rascunho</>}
                    </button>
                  ))}
                </div>
              </div>

              {/* Erro */}
              {erroForm && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-4">
                  <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5"/>
                  <p className="text-sm text-red-700">{erroForm}</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white border-t border-gray-100 p-5 flex gap-3">
              <button onClick={fecharModal} disabled={salvando}
                className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors disabled:opacity-50">
                Cancelar
              </button>
              <button onClick={salvar} disabled={salvando || uploadingImg}
                className="flex-1 px-4 py-3 rounded-xl font-bold text-sm text-[#1a1a1c] hover:shadow-lg transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                style={{ background: 'linear-gradient(135deg,#f4d03f,#d4af37)' }}>
                {salvando
                  ? <><Loader2 className="w-4 h-4 animate-spin"/> Salvando…</>
                  : <><Send className="w-4 h-4"/> {editando ? 'Salvar alterações' : 'Criar publicação'}</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GerenciamentoNovidades;